import { NftsToBuyMoleculeReatom } from "@/app/nft/buy/nfts-to-buy/molecule.reatom"
import type { PurchasableNft } from "@/app/nft/buy/nfts-to-buy/scope"
import { getBaseNftListEventContext } from "@/app/nft/utils/get-base-nft-event-context"
import { BlockchainBalanceSdkProviderMoleculeReatom } from "@/app/sdk/blockchains/blockchain-balance-sdk-molecule.reatom"
import { WalletConnectMoleculeReatom } from "@/app/wallet-connect/store.reatom"
import { LoggerMolecule } from "@/instrumentation/logger/logger-molecule"
import { type EventContext, Flow } from "@/instrumentation/logger/types"
import * as amplitude from "@amplitude/analytics-browser"
import type { PriceValue } from "@anaconda/core/price/types"
import { parseUnionAddress } from "@rarible_int/anaconda_sdk/ethereum"
import {
  type SolanaBuyRequest,
  buyFactoryReatom,
} from "@rarible_int/anaconda_sdk/solana"
import { sonnerToast } from "@rarible_int/anaconda_ui_components"
import { action, withAsync, withCallHook, wrap } from "@reatom/core"
import Big from "big.js"
import { molecule } from "bunshi"
import { SolanaSdkContainerMoleculeReatom } from "../../blockchains/solana-sdk-container-molecule.reatom"
import { BatchBuyToast } from "../../toasts/batch-buy-toast"
import {
  TransactionEvent,
  getTransactionErrorMessage,
} from "../events/transaction-event"
import { invalidateBoughtNftQueries } from "../order/invalidate-bought-nft-queries"

export interface SolanaBatchBuyActionParams {
  nfts: PurchasableNft[]
  price: PriceValue | undefined
}

export interface SolanaBatchBuyActionResult {
  txId: string
  nfts: PurchasableNft[]
  price: PriceValue | undefined
}

export const SolanaBatchBuyTransactionMoleculeReatom = molecule((mol) => {
  const logger = mol(LoggerMolecule)
  const { sdkContainerAtom } = mol(SolanaSdkContainerMoleculeReatom)
  const { solanaAddress: solanaAddressAtom } = mol(WalletConnectMoleculeReatom)
  const { balanceSDKAtom } = mol(BlockchainBalanceSdkProviderMoleculeReatom)
  const { cancelNftSelection } = mol(NftsToBuyMoleculeReatom)

  const batchBuyAction = action(
    async ({ nfts, price }: SolanaBatchBuyActionParams) => {
      const from = solanaAddressAtom()
      const container = sdkContainerAtom()
      const balanceSDK = balanceSDKAtom()

      if (!balanceSDK) {
        throw new Error("Balance SDK is not initialized for Solana batch buy")
      }
      if (!from) {
        throw new Error("Solana wallet address is not available for batch buy")
      }
      if (nfts.length === 0) {
        throw new Error("No NFTs to buy")
      }

      let balance: string | undefined
      try {
        balance = await balanceSDK.getNativeBalance({
          blockchain: nfts[0].collection.blockchain,
          address: from as `0x${string}`,
        })
      } catch {
        balance = undefined
      }

      if (balance && price && Big(price.amount).gt(balance)) {
        throw new Error("Error: insufficient funds")
      }

      const buy = buyFactoryReatom(container)
      let lastTxId = ""

      for (const nft of nfts) {
        const {
          address: collectionId,
          blockchain,
          tokenId,
        } = parseUnionAddress(nft.id)

        const request: SolanaBuyRequest = {
          from,
          blockchain,
          collectionId,
          tokenId,
          order: nft.bestSellOrder,
        }

        const result = await wrap(buy(request))
        lastTxId = result.txId
      }

      return { txId: lastTxId, nfts, price }
    },
    "solanaBatchBuyAction",
  ).extend(
    withAsync(),
    withCallHook(async (values, [params]) => {
      const { nfts, price } = params

      sonnerToast.custom(
        (id) => <BatchBuyToast.Pending nfts={nfts} price={price} id={id} />,
        {
          duration: Number.POSITIVE_INFINITY,
        },
      )

      const batchTransactionEventContext: EventContext = {
        flow: Flow.MultipleBuy,
      }

      logger?.api.pushEvent(
        TransactionEvent.Started,
        batchTransactionEventContext,
      )
      amplitude.track(TransactionEvent.Started, batchTransactionEventContext)
    }),
  )

  batchBuyAction.onFulfill.extend(
    withCallHook((result) => {
      const { txId, nfts, price } = result.payload

      if (nfts.length <= 0 || !txId) {
        return
      }

      for (const nft of nfts) {
        invalidateBoughtNftQueries(nft)
      }

      cancelNftSelection()

      const collection = nfts[0].collection
      const batchTransactionEventContext: EventContext = {
        flow: Flow.MultipleBuy,
      }

      const eventContext = {
        ...batchTransactionEventContext,
        ...getBaseNftListEventContext({
          ids: nfts.map((nft) => nft.id),
          price,
          collection,
        }),
        transactionId: txId,
      }

      logger?.api.pushEvent(TransactionEvent.Completed, eventContext)
      amplitude.track(TransactionEvent.Completed, eventContext)

      sonnerToast.dismiss()
      sonnerToast.custom((id) => (
        <BatchBuyToast.Success
          price={price}
          nfts={nfts}
          id={id}
          transactionId={txId}
        />
      ))
    }),
  )

  batchBuyAction.onReject.extend(
    withCallHook(({ error, params: [paramsValue] }) => {
      console.error("Solana batch buy failed:", error)

      const { nfts, price } = paramsValue

      const batchTransactionEventContext: EventContext = {
        flow: Flow.MultipleBuy,
      }

      const eventContext = {
        ...batchTransactionEventContext,
        error: getTransactionErrorMessage(error),
      }

      logger?.api.pushEvent(TransactionEvent.Failed, eventContext)
      amplitude.track(TransactionEvent.Failed, eventContext)

      sonnerToast.dismiss()

      if (nfts.length <= 0) {
        return
      }

      sonnerToast.custom((id) => (
        <BatchBuyToast.Error
          nfts={nfts}
          id={id}
          price={price}
          error={error as Error}
        />
      ))
    }),
  )

  return { batchBuyAction }
})
